/**
 * useKeyboardShortcuts Hook
 *
 * Registers global keyboard shortcuts for navigation and command palette.
 * Navigation shortcuts are suppressed when the user is typing in an input or textarea.
 * Ctrl+K / Cmd+K always works, even inside inputs.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface UseKeyboardShortcutsReturn {
  isCommandPaletteOpen: boolean;
  setCommandPaletteOpen: (open: boolean) => void;
  isShortcutsDialogOpen: boolean;
  setShortcutsDialogOpen: (open: boolean) => void;
}

export function useKeyboardShortcuts(): UseKeyboardShortcutsReturn {
  const [isCommandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [isShortcutsDialogOpen, setShortcutsDialogOpen] = useState(false);
  const navigate = useNavigate();

  const isInputFocused = useCallback((): boolean => {
    const el = document.activeElement;
    if (!el) return false;
    const tagName = el.tagName.toLowerCase();
    if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
      return true;
    }
    if ((el as HTMLElement).isContentEditable) {
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+K / Cmd+K: Open command palette (always works, even in inputs)
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        e.stopPropagation();
        setCommandPaletteOpen((prev) => !prev);
        return;
      }

      // Do not trigger navigation or dialog shortcuts when typing in inputs
      if (isInputFocused()) return;

      // "?" key: Open shortcuts dialog
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setShortcutsDialogOpen(true);
        return;
      }

      // Alt+1: Dashboard
      if (e.altKey && e.key === '1') {
        e.preventDefault();
        navigate('/dashboard');
        return;
      }

      // Alt+2: Patients
      if (e.altKey && e.key === '2') {
        e.preventDefault();
        navigate('/patients');
        return;
      }

      // Alt+3: Calendar
      if (e.altKey && e.key === '3') {
        e.preventDefault();
        navigate('/calendar');
        return;
      }

      // Alt+4: Sessions
      if (e.altKey && e.key === '4') {
        e.preventDefault();
        navigate('/sessions');
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [navigate, isInputFocused]);

  return {
    isCommandPaletteOpen,
    setCommandPaletteOpen,
    isShortcutsDialogOpen,
    setShortcutsDialogOpen,
  };
}
